import { useEffect } from 'react';
import MuiTable from '@mui/material/Table';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OwnerIcon from '@components/OwnerIcon';
import Tooltip from '@components/Tooltip';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import { Typography } from '@components/Typography';
import { Checkbox } from '@components/Checkbox';
import { Switch } from '@components/Switch';
import { styled } from '@mui/material/styles';
import Skeleton from '@mui/material/Skeleton';
import TableFooter from '@mui/material/TableFooter';
import TablePagination from '@mui/material/TablePagination';
import { useTranslation } from '@hooks';
import { PAGE_SIZE_OPTIONS } from '@constants/pagination';
import TableRowPlain from './TableRowPlain';
import TableRowWithActions from './TableRowWithActions';
import TablePaginationActions from './TablePaginationActions';
import TableCellCheckbox from './TableCellCheckbox';

const TableHeaderCell = styled(TableCell)(() => ({
    borderBottom: 'none',
    padding: '12px 16px',
}));

const TableCellMessage = styled(TableCell)(() => ({
    backgroundColor: 'rgba(228, 234, 235, 1)',
    padding: '12px 16px 12px 56px',
    lineHeight: '16px',
    borderBottom: 'none',
}));

export const StyledTablePagination = styled(TablePagination)(() => ({
    '& .MuiTablePagination-input': {
        fontSize: 14,
    },
    '& .MuiToolbar-root': {
        minHeight: 45,
    },
    '& .MuiTablePagination-selectLabel': {
        margin: '8px 0',
    },
    '& .MuiTablePagination-displayedRows': {
        margin: '8px 0',
    },
}));

export const getPaginationLabels = t => ({
    labelDisplayedRows: ({ from, to, count }) =>
        `${from}–${to} ${t('Table.of')} ${count !== -1 ? count : `${t('Table.moreThan')} ${to}`}`,
    labelRowsPerPage: <span>{t('Table.rowsPerPage')}</span>,
});

export const HintedHeader = ({ label, hint, compact = false }) => (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {/* A wrapped label keeps its full shrunk width, stranding the icon at the box edge. */}
        <Typography variant="subtitle2" sx={compact ? { width: 'min-content' } : { whiteSpace: 'nowrap' }}>
            {label}
        </Typography>
        <Tooltip title={hint} placement="top" arrow>
            <InfoOutlinedIcon
                sx={{ fontSize: 16, color: 'var(--text-secondary)' }}
                onClick={event => event.stopPropagation()}
            />
        </Tooltip>
    </div>
);

export const ToggleCell = ({ checked, disabled, onChange, hint = '' }) => (
    <div onClick={event => event.stopPropagation()} style={{ display: 'inline-flex' }}>
        <Tooltip title={hint} placement="top" arrow>
            {/* span keeps the tooltip working when the switch is disabled */}
            <span>
                <Switch checked={checked} disabled={disabled} handleChange={onChange} sx={{ margin: 0 }} />
            </span>
        </Tooltip>
    </div>
);

export const OwnerCell = ({ type, name, sx }) => (
    <Typography variant="body2" align="left" sx={{ display: 'flex', alignItems: 'center', gap: '4px', ...sx }}>
        <OwnerIcon type={type} />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
    </Typography>
);

export const Table = ({
    className,
    columnNames = [],
    data = [],
    rows = [],
    isDataLoading,
    onPrimaryAction,
    onSecondaryAction,
    isInteractive,
    isCustomActionComponent,
    customDataRows = [],
    withHover = false,
    withPagination = false,
    paginationProps = {},
    withCheckbox = false,
    entity = '',
    selectedItems = { list: {}, all: false },
    setSelectedItems,
    CollapsibleRow,
    isCollapsible = false,
    withTotalRow = false,
    sort,
    onSortChange,
    onRowClick,
    getRowKey,
}) => {
    const { t } = useTranslation();

    useEffect(() => {
        if (withCheckbox) {
            setSelectedItems(prev => ({ ...prev, list: {}, all: false }));
        }
    }, [data, withCheckbox]);

    const onSelectAll = e => {
        if (e.target.checked) {
            const newSelected = {};
            data.forEach(item => (newSelected[item?.id] = true));
            setSelectedItems(prev => ({ ...prev, list: newSelected }));
            return;
        }
        setSelectedItems(prev => ({ ...prev, list: {}, all: false }));
    };

    const onSelectAllData = () => setSelectedItems(prev => ({ ...prev, all: true }));

    const onClearAllDataSelection = () => setSelectedItems(prev => ({ ...prev, list: {}, all: false }));

    const onSelect = indexChecked => {
        const newItemId = data[indexChecked].id;
        const objectCopy = { ...selectedItems.list };
        if (!objectCopy[newItemId]) {
            objectCopy[newItemId] = true;
        } else {
            delete objectCopy[newItemId];
        }
        setSelectedItems(prev => ({ ...prev, list: objectCopy }));
    };

    const rowKey = (row, index) => {
        const item = data[index];
        return item && getRowKey ? getRowKey(item, index) : `row-${index}-${row[0]?.value || ''}`;
    };

    const selectedLength = Object.keys(selectedItems.list).length;

    return (
        <MuiTable className={className} sx={{ maxWidth: '100%' }}>
            <TableHead>
                <TableRow>
                    {withCheckbox && (
                        <TableCellCheckbox sx={{ borderBottom: 'none' }}>
                            <Checkbox
                                checked={(selectedLength > 0 && selectedLength === data.length) || selectedItems.all}
                                indeterminate={selectedLength > 0 && selectedLength !== data.length}
                                onChange={e => onSelectAll(e)}
                            />
                        </TableCellCheckbox>
                    )}
                    {columnNames.map((th, index) => {
                        const content = th.component || th.data || th;
                        const isSortable = !!th.sortKey && !!onSortChange;
                        const isActive = isSortable && sort?.column === th.sortKey;
                        return (
                            <TableHeaderCell
                                key={th.key || index}
                                align={th.align || (index > 0 ? 'center' : 'left')}
                                width={th.width || 0}
                                sx={th.sx}
                            >
                                {isSortable ? (
                                    <TableSortLabel
                                        active={isActive}
                                        direction={isActive ? sort.dir : 'desc'}
                                        onClick={() => onSortChange(th.sortKey)}
                                    >
                                        {content}
                                    </TableSortLabel>
                                ) : (
                                    content
                                )}
                            </TableHeaderCell>
                        );
                    })}
                </TableRow>
                {withCheckbox &&
                    !!selectedLength &&
                    selectedLength === paginationProps.rowsPerPage &&
                    !selectedItems.all &&
                    paginationProps.count > paginationProps.rowsPerPage && (
                        <TableRow>
                            <TableCellMessage colSpan={columnNames.length + 1}>
                                <Typography variant="caption" color="textSecondary">
                                    {t('Table.allOnPageSelected', { selected: data.length, entity: entity })}
                                </Typography>
                                <Typography variant="caption" color="primary">
                                    <span style={{ fontWeight: '500', cursor: 'pointer' }} onClick={onSelectAllData}>
                                        {t('Table.selectAll', { data: paginationProps.count, entity: entity })}
                                    </span>
                                </Typography>
                            </TableCellMessage>
                        </TableRow>
                    )}
                {withCheckbox && selectedItems.all && (
                    <TableRow>
                        <TableCellMessage colSpan={columnNames.length + 1}>
                            <Typography variant="caption" color="textSecondary">
                                {t('Table.allDataSelected', { data: paginationProps.count, entity: entity })}
                            </Typography>
                            <Typography variant="caption" color="primary">
                                <span
                                    style={{ fontWeight: '500', cursor: 'pointer' }}
                                    onClick={onClearAllDataSelection}
                                >
                                    {t('Table.clearSelection')}
                                </span>
                            </Typography>
                        </TableCellMessage>
                    </TableRow>
                )}
            </TableHead>
            {isDataLoading ? (
                <TableBody>
                    <TableRow>
                        <TableHeaderCell colSpan={withCheckbox ? columnNames.length + 1 : columnNames.length}>
                            <Skeleton variant="rectangular" width="100%" height={118} />
                        </TableHeaderCell>
                    </TableRow>
                </TableBody>
            ) : (
                <TableBody>
                    {!rows.length ? (
                        <TableRow>
                            <TableCell colSpan={columnNames.length} align="center">
                                {t('common.noData')}
                            </TableCell>
                        </TableRow>
                    ) : (
                        rows?.map((row, index) =>
                            row ? (
                                isInteractive ? (
                                    <TableRowWithActions
                                        key={rowKey(row, index)}
                                        rowData={row}
                                        onPrimaryAction={onPrimaryAction}
                                        onSecondaryAction={onSecondaryAction}
                                        isCustomActionComponent={isCustomActionComponent}
                                        withCheckbox={withCheckbox}
                                        checked={selectedItems.list[data[index]?.id] || selectedItems.all}
                                        onSelect={() => onSelect(index)}
                                        onRowClick={onRowClick ? () => onRowClick(data[index], index) : undefined}
                                    />
                                ) : (
                                    <TableRowPlain
                                        key={rowKey(row, index)}
                                        hover={withHover}
                                        onClick={onRowClick ? () => onRowClick(data[index], index) : undefined}
                                        rowData={row}
                                        dataRow={customDataRows.includes(index) ? true : false}
                                        withCheckbox={withCheckbox}
                                        checked={selectedItems.list[data[index]?.id] || selectedItems.all}
                                        onSelect={() => onSelect(index)}
                                        collapsibleRow={isCollapsible && <CollapsibleRow data={data} index={index} />}
                                        isCollapsible={isCollapsible}
                                        colSpan={columnNames.length}
                                        withTotalRow={withTotalRow}
                                        isLastRow={index === rows.length - 1}
                                    />
                                )
                            ) : null,
                        )
                    )}
                </TableBody>
            )}
            {withPagination && (
                <TableFooter>
                    <TableRow>
                        <StyledTablePagination
                            {...paginationProps}
                            rowsPerPageOptions={paginationProps.rowsPerPageOptions ?? PAGE_SIZE_OPTIONS}
                            slotProps={{ select: { inputProps: { 'aria-label': 'rows per page' }, native: true } }}
                            ActionsComponent={props => (
                                <TablePaginationActions {...props} pages={paginationProps.pages} />
                            )}
                        />
                    </TableRow>
                </TableFooter>
            )}
        </MuiTable>
    );
};
